import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import httpApi from 'hooks/api/httpApi';

import type { Translation, UpdateTranslationsBody } from 'features/orders/models/Translation';
import type { RootState } from 'redux/store';

export interface TranslationState {
    disabled: boolean;
    translations?: Translation[];
    loading: boolean;
}

const initialState: TranslationState = {
    disabled: true,
    loading: false,
};

export const getTranslations = createAsyncThunk<Translation[], void, { rejectValue: string }>(
    'translation/get',
    async (_, { rejectWithValue, getState }) => {
        try {
            const {
                ecoms: { id: ecomId },
            } = getState() as RootState;

            const response = await httpApi.get<Translation[]>(`/api/ecom/${ecomId}/translation`);

            const translations = response.getData();

            return translations;
        } catch (error: unknown) {
            return rejectWithValue('Error');
        }
    }
);

export const updateTranslations = createAsyncThunk<void, UpdateTranslationsBody['elements'], { rejectValue: string }>(
    'translation/update',
    async (translations, { rejectWithValue, getState, dispatch }) => {
        const {
            ecoms: { id: ecomId },
        } = getState() as RootState;

        return httpApi
            .post<Translation[]>(
                `/api/ecom/${ecomId}/translation/save`,
                translations.map(({ id, keyword, translations }) => ({
                    id,
                    keyword,
                    translations: translations.map(({ id, language, label }) => ({ id, label, language })),
                }))
            )
            .then(() => {
                dispatch(getTranslations());
            })
            .catch((error) => {
                rejectWithValue(error);
            });
    }
);

export const translationSlice = createSlice({
    name: 'translation',
    initialState: initialState,
    extraReducers: (builder) => {
        builder.addCase(getTranslations.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(
            getTranslations.fulfilled,
            (state: TranslationState, { payload }: PayloadAction<Translation[]>) => {
                state.translations = payload;
                state.loading = false;
            }
        );
        builder.addCase(getTranslations.rejected, (state) => {
            state.loading = false;
        });
        builder.addCase(updateTranslations.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateTranslations.fulfilled, (state: TranslationState) => {
            state.loading = false;
        });
        builder.addCase(updateTranslations.rejected, (state) => {
            state.loading = false;
        });
    },
    reducers: {
        toggleDisable: (state: TranslationState, { payload }: PayloadAction<boolean | undefined>) => {
            state.disabled = payload ?? !state.disabled;
        },
    },
});

export const { toggleDisable } = translationSlice.actions;

export default translationSlice.reducer;
